import { type Tag, type Trail, type TrailCapture } from '@prisma/client'
import React from 'react'
import TrailCard from '#app/components/trail-card.tsx'
import { cn } from '#app/lib/utils'

export interface TrailWithRequiredCapture extends Omit<
	Trail,
	'defaultCaptureId' | 'createdAt' | 'updatedAt'
> {
	defaultCapture: Omit<
		TrailCapture,
		'trailId' | 'ownerId' | 'createdAt' | 'updatedAt'
	> & { tags: { tag: Omit<Tag, 'createdAt' | 'updatedAt'> }[] }
}

type CaptureWithTags = Omit<
	TrailCapture,
	'ownerId' | 'createdAt' | 'updatedAt'
> & {
	trailId: string
	tags: { tag: Omit<Tag, 'createdAt' | 'updatedAt'> }[]
}

interface CommonProps {
	className?: string
	tagLinks?: boolean
	showCaptureDescription?: boolean
}

interface ByTrails extends CommonProps {
	trails: TrailWithRequiredCapture[]
	captures?: never
	trailsById?: never
}

interface ByCaptures extends CommonProps {
	captures: CaptureWithTags[]
	trailsById: Record<
		string,
		Omit<Trail, 'defaultCaptureId' | 'createdAt' | 'updatedAt'>
	>
	trails?: never
}

type TrailCardsProps = ByTrails | ByCaptures

const TrailCards: React.FC<TrailCardsProps> = ({
	trailsById,
	trails,
	captures,
	className,
	tagLinks,
	showCaptureDescription = false,
}) => {
	const cards = trails
		? trails.map((trail) => (
				<TrailCard
					key={trail.id}
					role="listitem"
					trail={trail}
					trailCapture={trail.defaultCapture}
					showCaptureDescription={showCaptureDescription}
					tagLinks={tagLinks}
				/>
			))
		: captures.map((capture) => (
				<TrailCard
					key={capture.id}
					role="listitem"
					trail={trailsById[capture.trailId]!}
					trailCapture={capture}
					showCaptureDescription={showCaptureDescription}
					tagLinks={tagLinks}
				/>
			))

	return (
		<div role="list" className={cn('gap-2 gap-y-0', className)}>
			{cards}
		</div>
	)
}

export default TrailCards
